import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useApplyOverride, useReassignOptions } from "../api/use-runs";
import { formatEntityLabel } from "../lib/format-entity-label";
import type { ReassignFitStatus, ReassignOption } from "../types";

interface ReassignModalProps {
  scheduleId: string;
  runId: string;
  studentIdentifier: string;
  courseId: string;
  /** Friendly student name (when known) so the title/toast read "Name (ID)". */
  studentName?: string | null;
  /** Friendly course title (when known) so the subhead reads "Name (ID)". */
  courseName?: string | null;
  currentSectionLabel: string | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Centered modal that surfaces reassignment options for a single (student,
 * course) pair and POSTs a manual override on click. Mounted at the page
 * level (`schedule-home-page`) so a single instance services every trigger
 * (event panel rosters, section panel rosters, validation issues "Reassign"
 * actions).
 *
 * Built on the native `<dialog>` element so we get a11y semantics (focus trap,
 * ESC-to-close, `aria-modal`) "for free". Focus management:
 *   - On open we capture `document.activeElement` so we can restore focus to
 *     the trigger when the modal closes.
 *   - Once the options query first resolves we move focus to the first option
 *     card so the user can pick with the keyboard immediately.
 *   - Closing the dialog (ESC, backdrop click, X, Cancel, success) restores
 *     focus on the next tick (`setTimeout(0)` — same pattern as `SidePanel`).
 *
 * Permission gating lives at every call site (the trigger is hidden for
 * Viewers); the modal does NOT defend against being mounted under a Viewer
 * role. If a future surface bypasses that contract, fix the trigger — not
 * this primitive.
 */
export function ReassignModal({
  scheduleId,
  runId,
  studentIdentifier,
  courseId,
  studentName,
  courseName,
  currentSectionLabel,
  isOpen,
  onClose,
}: ReassignModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const firstOptionRef = useRef<HTMLButtonElement | null>(null);
  // Tracks whether we've already focused the first option for this open
  // session, so a refetch (or a re-render that re-renders the same options)
  // doesn't steal focus mid-keyboard-navigation.
  const hasFocusedFirstRef = useRef(false);

  const optionsQuery = useReassignOptions(
    scheduleId,
    isOpen ? runId : undefined,
    isOpen ? studentIdentifier : undefined,
    isOpen ? courseId : undefined,
  );

  // Per-call success/error callbacks (passed to `mutate(..., { onSuccess })`)
  // close over the picked option, which is what we need for the toast copy.
  const applyOverride = useApplyOverride(scheduleId);

  const sectionDisplay = currentSectionLabel ?? "—";

  // Sync the native dialog's open state with the `isOpen` prop. Capture the
  // previously-focused element BEFORE showing so we can restore on close.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      lastFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
      hasFocusedFirstRef.current = false;
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Restore focus to the trigger after the dialog closes. The `close` event
  // fires both when ESC dismisses the dialog AND when our prop-sync effect
  // calls `dialog.close()`; either way focus should land back on the trigger.
  // setTimeout(0) so the focus call runs after React unmount work.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => {
      window.setTimeout(() => {
        const prev = lastFocusedRef.current;
        if (prev && typeof prev.focus === "function") {
          prev.focus();
        }
        // Drop the captured target so the NEXT open captures a fresh trigger.
        lastFocusedRef.current = null;
      }, 0);
    };
    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, []);

  // ESC / cancel handling. Native `<dialog>` fires a `cancel` event on ESC;
  // jsdom doesn't route it reliably, so we also listen for the key directly.
  // Either path calls `onClose` so React state stays in sync with the dialog's
  // open state.
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dialog.open) {
        e.preventDefault();
        onClose();
      }
    };
    dialog.addEventListener("cancel", handleCancel);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Initial focus: once the options query first surfaces a non-empty list,
  // move focus to the first option button so the user can pick via keyboard
  // without a Tab. Skipped on empty / loading / error states (those have
  // their own focusable elements — Cancel button is the safe fallback).
  useEffect(() => {
    if (!isOpen) return;
    if (hasFocusedFirstRef.current) return;
    const first = firstOptionRef.current;
    if (first) {
      first.focus();
      hasFocusedFirstRef.current = true;
    }
  }, [isOpen, optionsQuery.data]);

  const handlePick = (option: ReassignOption) => {
    if (applyOverride.isPending) return;
    applyOverride.mutate(
      {
        runId,
        request: {
          studentIdentifier,
          courseId: option.courseId,
          sectionLabel: option.sectionLabel,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            `Reassigned ${formatEntityLabel(
              studentName,
              studentIdentifier,
            )} to ${formatEntityLabel(option.courseName, option.courseId)} Sec ${
              option.sectionLabel ?? "—"
            }.`,
          );
          onClose();
        },
        onError: (err) => {
          const message =
            err instanceof Error ? err.message : "Failed to reassign student";
          toast.error(message);
        },
      },
    );
  };

  // Backdrop click — clicking the dialog element itself (not a descendant)
  // means the click landed on the browser-rendered backdrop. The inner chrome
  // div catches descendant clicks via its own click handler short-circuit.
  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) {
      onClose();
    }
  };

  const options = optionsQuery.data ?? [];

  const body = optionsQuery.isLoading ? (
    <div
      data-testid="reassign-modal-loading"
      className="py-10 flex justify-center"
    >
      <LoadingSpinner size="lg" />
    </div>
  ) : optionsQuery.isError ? (
    <ReassignOptionsError
      message={
        optionsQuery.error instanceof Error
          ? optionsQuery.error.message
          : "Failed to load reassignment options."
      }
      onRetry={() => optionsQuery.refetch()}
    />
  ) : options.length === 0 ? (
    <EmptyOptionsBody courseId={courseId} courseName={courseName} />
  ) : (
    <div className="flex flex-col gap-2">
      {options.map((option, idx) => (
        <ReassignOptionCard
          key={`${option.courseId}::${option.sectionLabel ?? ""}`}
          option={option}
          disabled={applyOverride.isPending}
          onPick={() => handlePick(option)}
          buttonRef={idx === 0 ? firstOptionRef : undefined}
        />
      ))}
    </div>
  );

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="reassign-modal-title"
      aria-modal="true"
      data-testid="reassign-modal"
      onClick={handleBackdropClick}
      // Native `<dialog>` is centered by default via the user-agent stylesheet;
      // we reset the default `padding` / `margin` / `border` and set our own
      // surface tokens. `[&::backdrop]:bg-black/50` styles the browser
      // backdrop layer to match the rest of the system.
      className="m-auto bg-surface border border-border-subtle rounded-lg w-full max-w-[520px] max-h-[86vh] p-0 text-text-soft animate-fade-in [&::backdrop]:bg-black/50"
    >
      <div className="flex flex-col max-h-[86vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-[18px] border-b border-border-subtle">
          <div className="min-w-0">
            <h3
              id="reassign-modal-title"
              data-testid="reassign-modal-title"
              className="m-0 text-[17px] font-semibold text-white"
            >
              Reassign {formatEntityLabel(studentName, studentIdentifier)}
            </h3>
            <p
              data-testid="reassign-modal-subhead"
              className="mt-1 text-[13px] text-text-muted"
            >
              {currentSectionLabel !== null ? (
                <>
                  Currently in{" "}
                  <strong className="text-text-soft">
                    {formatEntityLabel(courseName, courseId)} · Section{" "}
                    {sectionDisplay}
                  </strong>
                  . Choose a target from their requested courses.
                </>
              ) : (
                <>
                  Not yet scheduled for{" "}
                  <strong className="text-text-soft">
                    {formatEntityLabel(courseName, courseId)}
                  </strong>
                  . Choose a section to place them into.
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close reassign modal"
            data-testid="reassign-modal-close"
            className="text-text-muted hover:text-white text-[20px] leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">{body}</div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-border-subtle">
          <span
            data-testid="reassign-modal-footer-note"
            className="text-[12px] text-text-faint"
          >
            Override applies to the current run only.
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="reassign-modal-cancel"
            className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-4 py-2 text-[14px] font-medium cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </dialog>
  );
}

interface ReassignOptionCardProps {
  option: ReassignOption;
  disabled: boolean;
  onPick: () => void;
  /** Optional ref forwarded to the underlying `<button>` — used to focus the
   * first option after the options query resolves. */
  buttonRef?: React.Ref<HTMLButtonElement>;
}

/**
 * One candidate-section card. Acts as a `<button>` so the entire row is
 * keyboard-activatable. The `isCurrent` flavor renders a blue outline + a
 * "Current" pill INSTEAD of a fit-status badge — the user already knows
 * that's where the student lives.
 */
function ReassignOptionCard({
  option,
  disabled,
  onPick,
  buttonRef,
}: ReassignOptionCardProps) {
  const isCurrent = option.isCurrent;
  const sectionLabel = option.sectionLabel ?? "—";
  const meta = `${option.teacherDisplayName ?? "—"} · ${
    option.roomName ?? "—"
  } · ${option.timeLabel}`;

  const cardClasses = [
    "w-full text-left bg-page border rounded-md px-3.5 py-3",
    "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
    isCurrent
      ? "border-blue-500/60 cursor-default"
      : "border-border-subtle hover:border-blue-500/40 hover:bg-page/60 cursor-pointer",
    disabled ? "opacity-60 cursor-not-allowed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={isCurrent ? undefined : onPick}
      disabled={disabled || isCurrent}
      aria-current={isCurrent ? "true" : undefined}
      data-testid={`reassign-option-${option.courseId}-${
        option.sectionLabel ?? "none"
      }`}
      className={cardClasses}
    >
      <div className="flex items-center justify-between mb-1">
        <span
          data-testid="reassign-option-course"
          className="font-mono text-[13px] font-semibold text-blue-300"
        >
          {formatEntityLabel(option.courseName, option.courseId)} · Sec{" "}
          {sectionLabel}
        </span>
        {isCurrent ? (
          <span
            data-testid="reassign-option-current-pill"
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300"
          >
            Current
          </span>
        ) : (
          <FitStatusBadge status={option.fitStatus} />
        )}
      </div>
      <div className="text-[13px] text-text-soft">{option.timeLabel}</div>
      <div className="text-[12px] text-text-muted mt-0.5">{meta}</div>
      {option.warning !== null && (
        <div
          data-testid="reassign-option-warning"
          className="text-[12px] text-amber-400 mt-2 flex items-center gap-1.5"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
            className="shrink-0"
          >
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
          <span>{option.warning}</span>
        </div>
      )}
    </button>
  );
}

interface FitStatusBadgeProps {
  status: ReassignFitStatus;
}

function FitStatusBadge({ status }: FitStatusBadgeProps) {
  const config = FIT_STATUS_CONFIG[status];
  return (
    <span
      data-testid={`reassign-fit-badge-${status}`}
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${config.className}`}
    >
      {config.label}
    </span>
  );
}

/**
 * Badge config keyed by {@link ReassignFitStatus}. Lookup table (vs a switch)
 * because we also want to render the label in tests without re-implementing
 * the mapping.
 */
const FIT_STATUS_CONFIG: Record<
  ReassignFitStatus,
  { label: string; className: string }
> = {
  Fits: {
    label: "Fits",
    className: "bg-emerald-500/20 text-emerald-300",
  },
  TimeConflict: {
    label: "Time conflict",
    className: "bg-red-500/20 text-red-300",
  },
  AtCapacity: {
    label: "At capacity",
    className: "bg-amber-500/20 text-amber-300",
  },
};

interface ReassignOptionsErrorProps {
  message: string;
  onRetry: () => void;
}

function ReassignOptionsError({ message, onRetry }: ReassignOptionsErrorProps) {
  return (
    <div
      data-testid="reassign-modal-error"
      className="bg-page border border-red-500/30 rounded-md p-4 text-[13px] text-red-400 flex items-center justify-between gap-3"
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        data-testid="reassign-modal-retry"
        className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-3 py-1.5 text-[12px] font-medium"
      >
        Retry
      </button>
    </div>
  );
}

interface EmptyOptionsBodyProps {
  courseId: string;
  courseName?: string | null;
}

function EmptyOptionsBody({ courseId, courseName }: EmptyOptionsBodyProps) {
  return (
    <div
      data-testid="reassign-modal-empty"
      className="bg-page border border-border-subtle rounded-md p-4 text-[13px] text-text-muted"
    >
      This student has no requested courses for{" "}
      {formatEntityLabel(courseName, courseId)}. Cancel and check their request
      list.
    </div>
  );
}
